import { ApiDimension, ApiMetric } from "@incendium/api";

// E-commerce excluded dimensions
const ecomExcludedDimension = [
  ApiDimension.DIMENSION_CRAWLER,
  ApiDimension.DIMENSION_PATH,
  ApiDimension.DIMENSION_LOCATION,
  ApiDimension.DIMENSION_JOB,
  ApiDimension.DIMENSION_JOB_CATEGORY,
  ApiDimension.DIMENSION_JOB_TITLE,
  ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
  ApiDimension.DIMENSION_LANDING_PAGE_PATH,
  ApiDimension.DIMENSION_LANDING_NEXT_PAGE_LOCATION,
  ApiDimension.DIMENSION_LANDING_NEXT_PAGE_PATH,
  ApiDimension.DIMENSION_DAYS_TO_SECOND_VISIT,
];

// Publisher compatible dimensions
const publisherCompatibleDimensions = [
  ApiDimension.DIMENSION_CHANNEL,
  ApiDimension.DIMENSION_SOURCE_BUCKET,
  ApiDimension.DIMENSION_SOURCE,
  ApiDimension.DIMENSION_MEDIUM,
  ApiDimension.DIMENSION_CAMPAIGN,
  ApiDimension.DIMENSION_CONTENT,
  ApiDimension.DIMENSION_TERM,
  ApiDimension.DIMENSION_SOURCE_CATEGORY,
];

// Common excluded dimensions for various metrics
const commonExcludedDimensions = [ApiDimension.DIMENSION_CRAWLER];

const productExcludedDimensions = [
  ApiDimension.DIMENSION_JOB,
  ApiDimension.DIMENSION_JOB_CATEGORY,
  ApiDimension.DIMENSION_JOB_TITLE,
  ApiDimension.DIMENSION_DAYS_TO_SECOND_VISIT,
];

const jobExcludedDimensions = [
  ApiDimension.DIMENSION_PRODUCT,
  ApiDimension.DIMENSION_PRODUCT_CATEGORY,
  ApiDimension.DIMENSION_PRODUCT_NAME,
  ApiDimension.DIMENSION_PRODUCT_BRAND,
  ApiDimension.DIMENSION_DAYS_TO_SECOND_VISIT,
];

export const getExcludedDimensions = (metric: ApiMetric): ApiDimension[] => {
  // Pageviews and sessions
  if (
    [
      ApiMetric.METRIC_PAGEVIEWS_COUNT,
      ApiMetric.METRIC_SESSIONS_COUNT,
      ApiMetric.METRIC_DESKTOP_SESSIONS_COUNT,
      ApiMetric.METRIC_MOBILE_SESSIONS_COUNT,
    ].includes(metric)
  ) {
    return [];
  }

  // Leads
  if (
    [
      ApiMetric.METRIC_LEADS_COUNT,
      ApiMetric.METRIC_NEW_LEADS_COUNT,
      ApiMetric.METRIC_RETURNING_LEADS_COUNT,
    ].includes(metric)
  ) {
    return commonExcludedDimensions;
  }

  // Sessions per lead and new/returning sessions
  if (
    [
      ApiMetric.METRIC_SESSION_COUNT_PER_LEAD,
      ApiMetric.METRIC_NEW_SESSIONS_COUNT,
      ApiMetric.METRIC_RETURNING_SESSIONS_COUNT,
    ].includes(metric)
  ) {
    return [
      ApiDimension.DIMENSION_CRAWLER,
      ApiDimension.DIMENSION_DAYS_TO_SECOND_VISIT,
    ];
  }

  // Returning lead percentage
  if (metric === ApiMetric.METRIC_RETURNING_LEAD_PERCENTAGE) {
    return commonExcludedDimensions;
  }

  // Customers
  if (
    [
      ApiMetric.METRIC_CUSTOMERS_COUNT,
      ApiMetric.METRIC_NEW_CUSTOMERS_COUNT,
      ApiMetric.METRIC_RETURNING_CUSTOMERS_COUNT,
      ApiMetric.METRIC_RETURNING_NINETY_DAY_CUSTOMERS_COUNT,
    ].includes(metric)
  ) {
    return commonExcludedDimensions;
  }

  // Returning ninety day customer percentage
  if (metric === ApiMetric.METRIC_RETURNING_NINETY_DAY_CUSTOMER_PERCENTAGE) {
    return commonExcludedDimensions;
  }

  // Product pageviews
  if (
    metric === ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT ||
    metric === ApiMetric.METRIC_SALE_ITEMS_QUANTITY ||
    metric === ApiMetric.METRIC_PRODUCT_PROFIT ||
    metric === ApiMetric.METRIC_AVERAGE_MARGIN_PERCENT
  ) {
    return productExcludedDimensions;
  }

  // Job pageviews
  if (metric === ApiMetric.METRIC_JOB_PAGEVIEWS_COUNT) {
    return jobExcludedDimensions;
  }

  // E-commerce metrics
  if (
    [
      ApiMetric.METRIC_VISIT_TO_PURCHASE_RATE,
      ApiMetric.METRIC_SPEND,
      ApiMetric.METRIC_CPA,
      ApiMetric.METRIC_CAC,
      ApiMetric.METRIC_CPNV,
    ].includes(metric)
  ) {
    return ecomExcludedDimension;
  }

  return [];
};

export const getCompatibleDimensions = (metric: ApiMetric): ApiDimension[] => {
  // Publisher metrics
  if (
    [
      ApiMetric.METRIC_PUBLISHER_CLICKS,
      ApiMetric.METRIC_PUBLISHER_CONVERSIONS,
      ApiMetric.METRIC_PUBLISHER_ALL_CONVERSIONS,
      ApiMetric.METRIC_PUBLISHER_SALES_COUNT,
      ApiMetric.METRIC_PUBLISHER_REVENUE,
      ApiMetric.METRIC_PUBLISHER_IMPRESSIONS,
      ApiMetric.METRIC_PUBLISHER_CTR,
      ApiMetric.METRIC_PUBLISHER_CPC,
    ].includes(metric)
  ) {
    return publisherCompatibleDimensions;
  }

  return [];
};

export const metricIsCompatible = (
  metric: ApiMetric,
  dimensions: ApiDimension[],
): boolean => {
  const excludedDimensions = getExcludedDimensions(metric);
  const compatibleDimensions = getCompatibleDimensions(metric);

  if (excludedDimensions.length > 0) {
    return !dimensions.some((d) => excludedDimensions.includes(d));
  }

  if (compatibleDimensions.length > 0) {
    return dimensions.every((d) => compatibleDimensions.includes(d));
  }

  return true;
};
